import i18n from 'i18next';

/* ---------------------------------------------------------------------------
   טקסטים שנערכו מקונסולת המפעיל (/admin ← תוכן), מעל קבצי התרגום.

   השרת מחזיר namespace → (מפתח מנוקד → טקסט), למשל
   public → { "landing.title": "...", "legal.terms.sections.3.body.0": "..." }.
   כאן מחילים אותם על i18next: כל namespace שנגעו בו נבנה מחדש מהמקור שבקוד
   ועליו הדריסות — כך גם טקסט ש"הוחזר למקור" בקונסולה חוזר בלי טעינה מחדש.

   המודול לא מייבא את lib/api (שמייבא את lib/i18n) — i18n.ts קורא לו באתחול.
   הטעינה מהשרת ב-content-sync.ts.
   --------------------------------------------------------------------------- */

export type ContentBundle = Record<string, Record<string, string>>;
type Tree = Record<string, unknown>;

/** מקטעים שאסור לכתוב לתוך אובייקט — דריסה מהשרת לא תגיע ל-prototype. */
const FORBIDDEN = new Set(['__proto__', 'prototype', 'constructor']);

let base: Record<string, Record<string, unknown>> = {};
const applied = new Map<string, Set<string>>();

/** המשאבים כפי שנטענו מה-bundle (אחרי השלמת צורות הרבים בעברית). */
export function setBaseResources(resources: Record<string, Record<string, unknown>>): void {
  base = resources;
}

const STORAGE_PREFIX = 'craftmind.content.';

/** הדריסות האחרונות שנטענו בדפדפן הזה — כדי שהטקסט הנכון יופיע כבר ברינדור הראשון. */
export function cachedContent(locale: string): ContentBundle | null {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + locale);
    return raw ? sanitize(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

export function storeContent(locale: string, bundle: ContentBundle): void {
  try {
    localStorage.setItem(STORAGE_PREFIX + locale, JSON.stringify(bundle));
  } catch {
    /* מצב פרטי — בפעם הבאה ייטען מהשרת */
  }
}

/** רק namespace ומפתחות מוכרים-בצורתם, וערכי מחרוזת. כל השאר נזרק בשקט. */
export function sanitize(input: unknown): ContentBundle {
  const out: ContentBundle = {};
  if (typeof input !== 'object' || input === null || Array.isArray(input)) return out;
  for (const [ns, entries] of Object.entries(input as Tree)) {
    if (!/^[a-z]+(-[a-z]+)*$/.test(ns) || typeof entries !== 'object' || entries === null) continue;
    const clean: Record<string, string> = {};
    for (const [key, value] of Object.entries(entries as Tree)) {
      if (typeof value !== 'string') continue;
      const parts = key.split('.');
      if (parts.some((p) => !/^[A-Za-z0-9_]+$/.test(p) || FORBIDDEN.has(p))) continue;
      clean[key] = value;
    }
    out[ns] = clean;
  }
  return out;
}

const isTree = (v: unknown): v is Tree => typeof v === 'object' && v !== null;

/** כותב ערך לנתיב מנוקד; רשימות נשארות רשימות ("sections.3.body.0"). */
export function setPath(tree: Tree, key: string, value: string): void {
  const parts = key.split('.');
  let node: Tree = tree;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i]!;
    if (FORBIDDEN.has(part)) return;
    const next = node[part];
    if (!isTree(next)) node[part] = /^\d+$/.test(parts[i + 1]!) ? [] : {};
    node = node[part] as Tree;
  }
  const last = parts[parts.length - 1]!;
  if (FORBIDDEN.has(last)) return;
  node[last] = value;
}

function getPath(tree: Tree, key: string): unknown {
  let node: unknown = tree;
  for (const part of key.split('.')) {
    if (!isTree(node)) return undefined;
    node = node[part];
  }
  return node;
}

/**
 * מחיל את הדריסות של שפה אחת. namespace שהייתה בו דריסה בפעם הקודמת ואין בו
 * עכשיו — חוזר למקור.
 */
export function applyContent(locale: string, bundle: ContentBundle): void {
  const source = base[locale];
  if (!source) return;
  const before = applied.get(locale) ?? new Set<string>();
  const touched = new Set([...before, ...Object.keys(bundle)]);
  const now = new Set<string>();

  for (const ns of touched) {
    const original = source[ns];
    if (!isTree(original)) continue;
    const tree = structuredClone(original);
    const entries = bundle[ns] ?? {};
    for (const [key, value] of Object.entries(entries)) {
      // עברית: _two / _many נגזרו מ-_other באתחול (i18n.ts). אם הם עדיין העתק
      // שלו — הם מתעדכנים יחד איתו; אם בקובץ יש להם נוסח משלהם — לא נוגעים.
      if (key.endsWith('_other')) {
        const stem = key.slice(0, -'_other'.length);
        const was = getPath(original, key);
        for (const form of ['_two', '_many'])
          if (getPath(original, stem + form) === was && !(stem + form in entries))
            setPath(tree, stem + form, value);
      }
      setPath(tree, key, value);
    }
    if (Object.keys(entries).length > 0) now.add(ns);
    i18n.removeResourceBundle(locale, ns);
    i18n.addResourceBundle(locale, ns, tree, false, true);
  }
  applied.set(locale, now);
}
